// Helper utility module for Meta Pixel (Facebook Pixel) tracking based on cookie consent

declare global {
  interface Window {
    fbq: any;
    _fbq: any;
    __metaPixelLoaded?: boolean;
    __metaPixelInitialized?: boolean;
    __metaPixelId?: string;
  }
}

// Safely retrieve the current user's cookie consent preference from localStorage
export function getMarketingConsent(): boolean {
  try {
    const consentPrefs = localStorage.getItem('cookie-consent-preferences');
    if (consentPrefs) {
      const parsed = JSON.parse(consentPrefs);
      return !!parsed.marketing;
    }
  } catch (e) {
    console.error('Error reading cookie context:', e);
  }
  return false;
}

// Local module variables to cache fetched identifiers
let cachedPixelId: string | null = null;

// Get the 17-digit Meta Pixel ID from client-side environments safely
export function getMetaPixelId(): string | null {
  const envId = import.meta.env.VITE_META_PIXEL_ID;
  if (envId && envId.trim()) {
    return envId.trim();
  }

  // Warn ONLY in development if missing
  if (import.meta.env.DEV) {
    console.warn('VITE_META_PIXEL_ID is not configured in environment variables.');
  }
  return null;
}

// Inject base Facebook Pixel initialization script into head
function injectMetaPixelScript() {
  if (window.__metaPixelLoaded) return;

  // Official Meta Pixel dynamic injection script block
  (function (f: any, b: Document, e: string, v: string, n?: any, t?: any, s?: any) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    if (s && s.parentNode) {
      s.parentNode.insertBefore(t, s);
    } else {
      b.head.appendChild(t);
    }
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

  window.__metaPixelLoaded = true;
}

// Initialize Meta Pixel instance safely with provided ID
export function initMetaPixel(pixelId: string) {
  if (!pixelId) return;

  if (!window.__metaPixelLoaded) {
    injectMetaPixelScript();
  }

  if (typeof window.fbq === 'function' && !window.__metaPixelInitialized) {
    window.fbq('init', pixelId);
    window.__metaPixelInitialized = true;
    window.__metaPixelId = pixelId;
  }
}

// Dynamically track custom events or standard PageViews
export function trackMetaPixelEvent(eventName: string, data?: any) {
  // If user has rejected marketing/targeting, we never track
  if (!getMarketingConsent()) {
    return;
  }

  if (window.__metaPixelInitialized && typeof window.fbq === 'function') {
    if (eventName === 'PageView') {
      window.fbq('track', 'PageView');
    } else {
      window.fbq('track', eventName, data);
    }
  } else {
    // Lazy initialization of the tag if consent is high and ID is available
    const id = getMetaPixelId();
    if (id && getMarketingConsent()) {
      initMetaPixel(id);
      if (typeof window.fbq === 'function') {
        if (eventName === 'PageView') {
          window.fbq('track', 'PageView');
        } else {
          window.fbq('track', eventName, data);
        }
      }
    }
  }
}

// Re-evaluate Meta Pixel state when customer updates consent preference
export function handleMetaPixelConsentChange(marketingGranted: boolean) {
  if (marketingGranted) {
    const id = getMetaPixelId();
    if (id) {
      initMetaPixel(id);
      // Force an immediate page view event tracking on explicit consent activation
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'PageView');
      }
    }
  } else {
    // If disabled, future event payloads are fully blocked in trackMetaPixelEvent
  }
}
